import { Alert, Button, Form, Input, Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthMethod } from '@nxsys/hooks/AuthHooks';
import {
  getTwoFactorStatus,
  reauthForTwoFactor,
  twoFactorErrorMessage,
  TwoFactorSessionResponse,
} from '@nxsys/services/auth/twoFactor';
import { ADMIN_LOGIN_AS, IS_BUREAU } from '@nxsys/store/constants/keys';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { showMessage } from '@nxsys/utils/message';
import TwoFactorSetup from '../../../modules/auth/TwoFactor/TwoFactorSetup';

/**
 * Mandatory enrolment for a session that predates 2FA (NX-5764).
 *
 * NX-4996 put the enrolment gate in the login request, so it is only ever
 * reached by someone typing a password. Anyone already signed in when 2FA
 * shipped kept working without enrolling — and kept working indefinitely, since
 * only New UI tokens carry a server-side expiry and nothing swept the older
 * sessions. This is the other half: on an established session, ask the same
 * question login would have asked.
 *
 * The server owns whether to ask at all. `/auth/2fa/status` reports `enforced`
 * straight from `TWO_FACTOR_ENFORCED`, so switching enforcement off in an
 * environment switches this off with it and nothing has to be rebuilt.
 *
 * **Scope, stated plainly:** this is a client-side prompt. It closes the route a
 * user actually takes, not the API underneath it — a request made outside the
 * app still succeeds while unenrolled. Blocking that needs the server to refuse,
 * which was considered for this ticket and deliberately left out (Thang,
 * 25/08/2026). Worth knowing before anyone reads this as a security boundary.
 */

/** Same rule the axios instance uses to pick a backend. */
const currentBaseUrl = (): string =>
  localStorage.getItem(IS_BUREAU) === 'true'
    ? (import.meta.env.VITE_API_URL_BUREAU as string)
    : (import.meta.env.VITE_API_URL as string);

/**
 * True while a super-admin is signed in **as** somebody else.
 *
 * They are exempt, and not as a courtesy: `TwoFactorController` refuses every
 * 2FA change on an impersonated session — enrolling included — so prompting
 * would corner the support user with a dialog that cannot be satisfied and no
 * way past it. `ADMIN_LOGIN_AS` holds the real admin's own session for the
 * journey back, and is cleared when they return to their own account.
 */
const isImpersonating = (): boolean => {
  try {
    return !!JSON.parse(localStorage.getItem(ADMIN_LOGIN_AS) || '{}')?.token;
  } catch {
    // Malformed JSON means we cannot tell, and the safe answer is to ask: a
    // genuine user who has not enrolled must not slip through on a parse error.
    return false;
  }
};

const AppTwoFactorEnforcer = () => {
  const dispatch = useDispatch();
  const { logout } = useAuthMethod();
  const [form] = Form.useForm<{ password: string }>();

  /** `null` until the status call answers, so nothing flashes on first paint. */
  const [required, setRequired] = useState<boolean | null>(null);
  const [challengeToken, setChallengeToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /**
   * The status call is a one-shot per mount. Without this, React 18's
   * development double-mount asks twice, and any later re-render that changed
   * the dependencies would ask again — a second prompt appearing behind the
   * wizard the user is already halfway through.
   */
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;

    if (isImpersonating()) {
      setRequired(false);
      return;
    }

    getTwoFactorStatus()
      // `mustEnrol` is the server's answer for this session, and it covers what
      // this component cannot see: a master-password login skips the challenge
      // at sign-in (D7), and being asked to enrol a moment later contradicts the
      // same decision — but the login response strips the flag that would say
      // so. `enforced && !enabled` is the fallback for a backend that predates
      // the field, which is exactly the rule that used to be here.
      .then(({ enabled, enforced, mustEnrol }) =>
        setRequired(mustEnrol ?? (enforced && !enabled)),
      )
      // A status call that fails must not lock the app behind a dialog the user
      // cannot dismiss. Staying quiet is the right failure here: enforcement at
      // login still stands, so the worst case is that this session is asked the
      // next time it signs in rather than now.
      .catch(() => setRequired(false));
  }, []);

  const handlePassword = useCallback(
    async ({ password }: { password: string }) => {
      setBusy(true);
      setError('');
      try {
        const { challengeToken: token } = await reauthForTwoFactor(password);
        setChallengeToken(token);
        form.resetFields();
      } catch (e) {
        setError(twoFactorErrorMessage(e, 'That password was not accepted.'));
      } finally {
        setBusy(false);
      }
    },
    [form],
  );

  /**
   * Enrolling bumps `tokenVersion`, which retires every token minted before it —
   * including the one this session is holding. Installing the session the wizard
   * hands back is what stops the user being signed out by their own enrolment.
   */
  const install = useCallback(
    (session: TwoFactorSessionResponse) => {
      if (session?.token && session?.user) {
        dispatch(
          appActions.setLoginData({
            token: session.token,
            user: session.user as any,
            isBureau: localStorage.getItem(IS_BUREAU) === 'true',
          }),
        );
      }
      showMessage({
        type: 'success',
        content: 'Two-factor authentication is on.',
      });
      setChallengeToken('');
      setRequired(false);
    },
    [dispatch],
  );

  /**
   * The challenge lapses after ten minutes, which is long enough for someone to
   * walk away mid-scan. Dropping back to the password step re-mints it in place,
   * rather than making them reload the page to get out of a dead wizard.
   */
  const restart = useCallback(() => {
    setChallengeToken('');
    setError('Your password check expired. Please enter your password again.');
  }, []);

  if (required !== true) return null;

  if (challengeToken) {
    return (
      <TwoFactorSetup
        baseUrl={currentBaseUrl()}
        challengeToken={challengeToken}
        onComplete={install}
        onExpired={restart}
        // No `onCancel`: from here enrolment is not optional, and the wizard
        // reads its absence as the mandatory path. `onLeave` is the one exit,
        // and for an established session leaving means ending it.
        onLeave={logout}
      />
    );
  }

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={460}
      title="Set up two-factor authentication"
      // Above TopBarStack (1100), which every other modal in the app sits under.
      // This one is the exception because step two is the enrolment wizard, and
      // that overlay already covers the banners — without this the interruption
      // would change shape halfway through, banners crisp on one step and gone
      // on the next.
      zIndex={1101}
      // The app behind is pushed back rather than left legible. antd's default
      // mask is a flat 45% black, which over a dashboard this dense still reads
      // as "carry on working" — and the step after this one is the wizard, which
      // covers the page outright. Dimming further and blurring makes the two
      // steps feel like one interruption instead of two different weights.
      styles={{
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <p>
        Two-factor authentication is now required on Nxsys. Confirm your
        password to set it up — it takes about two minutes.
      </p>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" onFinish={handlePassword}>
        <Form.Item
          name="password"
          label="Your password"
          rules={[{ required: true, message: 'Enter your password' }]}
        >
          <Input.Password
            autoComplete="current-password"
            placeholder="Password"
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={busy} block>
          Continue
        </Button>
        {/* The only way out, and it costs the session — which is the point: the
            alternative is carrying on unenrolled, and that is what this closes. */}
        <Button type="link" onClick={logout} block style={{ marginTop: 8 }}>
          Log out instead
        </Button>
      </Form>
    </Modal>
  );
};

export default AppTwoFactorEnforcer;
